import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Home, BookOpen, FileText, Highlighter, Brain, ClipboardList,
  Presentation, Clock, MessageSquare, GraduationCap, Map,
  PlayCircle, Sparkles, ArrowRight, CheckCircle2, ChevronDown,
  Star, Library, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// Curated demo unit (Grade 9 — Ancient Mesopotamia w/ Hammurabi seed text)
// Falls back to first G9 unit if the seeded id is missing.
const DEMO_UNIT_ID_FALLBACK = '2a7dbc99-4876-40b4-8cf1-3ab5d0371f30';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_STOPS = [
  { id: 'library',     icon: Library,        title: 'Curriculum Library',      tagline: 'Six grades. One coherent scope.' },
  { id: 'unit',        icon: BookOpen,       title: 'Unit Detail',             tagline: 'Every unit, every standard, every link.' },
  { id: 'sources',     icon: FileText,       title: 'Primary Sources',         tagline: 'Verified documents, every era.' },
  { id: 'annotate',    icon: Highlighter,    title: 'Annotation Tool',         tagline: 'Mark up the past. Make it stick.' },
  { id: 'quiz',        icon: Brain,          title: 'Vocabulary Quiz',         tagline: 'Spaced practice that builds fluency.' },
  { id: 'skeleton',    icon: ClipboardList,  title: 'Skeleton Notes',          tagline: 'Lecture-ready guided notes.' },
  { id: 'lecture',     icon: Presentation,   title: 'Lecture Presentation',    tagline: 'Slide-ready mini-lectures.' },
  { id: 'timeline',    icon: Clock,          title: 'Historical Timeline',     tagline: 'See cause and effect.' },
  { id: 'questions',   icon: MessageSquare,  title: 'Daily Questions',         tagline: 'Bell-ringer prompts, no prep.' },
  { id: 'essay',       icon: GraduationCap,  title: 'AI Essay Grader',         tagline: 'Rubric-aligned feedback in seconds.' },
  { id: 'maps',        icon: Map,            title: 'Map Explorer',            tagline: 'Place names become places.' },
  { id: 'video',       icon: PlayCircle,     title: 'Video Library',           tagline: '118 Crash Course + CNN10 + A-to-Z.' },
];

// ── Section frame ──────────────────────────────────────────────────────────
const Stop = ({ id, index, icon: Icon, title, tagline, children, onCta, ctaLabel = 'Try the live tool →' }) => (
  <section id={id} className="scroll-mt-32 py-20 lg:py-28 border-t border-[#EBE7E0]/60" data-testid={`tour-stop-${id}`}>
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-start gap-4 mb-8">
        <div className="shrink-0 w-12 h-12 rounded-full bg-[#0F1A2E] text-archive-paper flex items-center justify-center font-mono text-sm">
          {String(index).padStart(2, '0')}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Icon className="h-5 w-5 text-yellow-600" />
            <span className="text-xs uppercase tracking-widest text-[#5A6577]">{tagline}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-serif font-light tracking-tight text-[#14213D]">{title}</h2>
        </div>
      </div>
      <div className="grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-3 order-2 lg:order-1">{children}</div>
        <div className="lg:col-span-2 order-1 lg:order-2 lg:sticky lg:top-32">
          <Card className="p-6 bg-[#F9F8F4]/80 border-[#EBE7E0]">
            <p className="text-[#2D3748] leading-relaxed mb-6 text-sm">
              {STOP_DESCRIPTIONS[id]}
            </p>
            <Button
              variant="outline"
              className="w-full justify-between border-[#0F1A2E] hover:bg-[#0F1A2E] hover:text-archive-paper transition"
              onClick={onCta}
              data-testid={`tour-cta-${id}`}
            >
              {ctaLabel}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Card>
        </div>
      </div>
    </div>
  </section>
);

const STOP_DESCRIPTIONS = {
  library: 'Browse the entire HGSS curriculum from one library. Grade 7 Kansas History, Grade 8 Pre-Columbian–1877 America, Grades 9–10 World, Grade 11 US History, Grade 12 Government & Economics.',
  unit: 'Every unit pulls together a description, Kansas HGSS standards, vocabulary, lesson plans, essential questions, and rubrics — all on one page.',
  sources: 'Each unit comes with verified primary sources — speeches, maps, photographs, letters — linked to the Library of Congress, National Archives, and museum collections.',
  annotate: 'Highlight passages in five colors, leave private notes, and save annotation sets per student. Built for close reading.',
  quiz: 'Auto-generated multiple-choice quizzes from each unit\'s vocabulary. Students see immediate feedback; teachers see class-wide scores.',
  skeleton: 'Print-ready guided notes for in-class lectures. Fill-in-the-blank format keeps students engaged without writing fatigue.',
  lecture: 'Mini-lectures broken into 10–15 minute segments. Each slide includes overview, key points, and discussion prompts.',
  timeline: 'Visualize historical events, cause-and-effect chains, and connections across units. Pinch-zoom on touch devices.',
  questions: 'A fresh bell-ringer every day — primary-source or current-events themed — automatically generated from the unit you\'re teaching.',
  essay: 'Submit a student essay, get rubric-aligned grades, strengths, areas for improvement, plagiarism detection, and AI-writing indicators in under 30 seconds.',
  maps: 'Interactive Kansas geography map and global Map Explorer with markers for battles, treaties, migrations, and cities.',
  video: '118 Crash Course episodes mapped unit-by-unit, plus CNN 10 daily news and World From A to Z curated series.',
};

// ── Inline previews ────────────────────────────────────────────────────────
const LibraryPreview = () => {
  const grades = [
    { g: 7,  label: 'Kansas History',           units: 12, color: 'bg-[#F9F8F4] text-[#14213D] border-[#EBE7E0]' },
    { g: 8,  label: 'Pre-Columbian – 1877',     units: 14, color: 'bg-orange-100 text-orange-900 border-orange-200' },
    { g: 9,  label: 'Ancient & Medieval World', units: 12, color: 'bg-emerald-100 text-emerald-900 border-emerald-200' },
    { g: 10, label: 'Modern World',             units: 12, color: 'bg-sky-100 text-sky-900 border-sky-200' },
    { g: 11, label: 'US History',               units: 14, color: 'bg-indigo-100 text-indigo-900 border-indigo-200' },
    { g: 12, label: 'Government & Economics',   units: 14, color: 'bg-rose-100 text-rose-900 border-rose-200' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {grades.map((it) => (
        <div
          key={it.g}
          className={`relative border rounded-lg p-4 ${it.color} transition-transform hover:-translate-y-0.5`}
          data-testid={`tour-grade-${it.g}`}
        >
          <div className="text-3xl font-serif font-light leading-none">G{it.g}</div>
          <div className="text-xs mt-2 font-medium leading-tight">{it.label}</div>
          <div className="text-xs opacity-70 mt-3">{it.units} units</div>
        </div>
      ))}
    </div>
  );
};

const UnitPreview = () => (
  <div className="bg-[#FDFBF7] rounded-xl border border-[#EBE7E0] p-6 shadow-sm">
    <div className="flex items-center gap-2 text-xs text-[#5A6577] mb-2">
      <Badge variant="outline" className="border-[#CBD5E0]">GRADE 9</Badge>
      <span>· Unit 01</span>
    </div>
    <h3 className="text-2xl font-serif text-[#14213D] mb-2">Ancient Mesopotamia</h3>
    <p className="text-sm text-[#5A6577] leading-relaxed mb-4">
      The world&apos;s first cities rose between the Tigris and Euphrates rivers around 3500 BCE.
      Students trace how irrigation, written law, and trade routes built — and toppled — the
      earliest civilizations.
    </p>
    <div className="grid grid-cols-3 gap-2 text-center pt-4 border-t border-[#EBE7E0]">
      {[['7', 'HGSS Standards'], ['12', 'Vocabulary'], ['8', 'Primary Sources']].map(([n, l]) => (
        <div key={l}>
          <div className="text-xl font-serif text-yellow-600">{n}</div>
          <div className="text-[11px] uppercase tracking-wider text-[#5A6577]">{l}</div>
        </div>
      ))}
    </div>
  </div>
);

const SourcesPreview = () => {
  const sources = [
    { title: 'The Code of Hammurabi',  type: 'Document', org: 'Louvre' },
    { title: 'The Standard of Ur',     type: 'Artifact', org: 'British Museum' },
    { title: 'Sumerian Cuneiform Tablet', type: 'Document', org: 'Yale Babylonian Collection' },
  ];
  return (
    <div className="space-y-2">
      {sources.map((s) => (
        <div key={s.title} className="flex items-center justify-between p-4 bg-[#FDFBF7] border border-[#EBE7E0] rounded-lg">
          <div>
            <div className="font-medium text-[#14213D] text-sm">{s.title}</div>
            <div className="text-xs text-[#5A6577] mt-0.5">{s.org}</div>
          </div>
          <Badge variant="outline" className="text-[11px]">{s.type}</Badge>
        </div>
      ))}
    </div>
  );
};

const AnnotatePreview = () => (
  <div className="bg-[#FDFBF7] rounded-xl border border-[#EBE7E0] p-6 shadow-sm relative">
    <div className="flex items-center gap-2 mb-4">
      {['bg-[#F5EBC0]', 'bg-emerald-200', 'bg-sky-200', 'bg-pink-200', 'bg-purple-200'].map((c) => (
        <button key={c} className={`w-6 h-6 rounded-full border border-[#CBD5E0] ${c}`} aria-label={`highlight ${c}`} disabled />
      ))}
      <span className="text-xs text-[#5A6577] ml-2">Highlight colors</span>
    </div>
    <p className="text-[#14213D] leading-relaxed text-sm font-serif">
      When <mark className="bg-[#F5EBC0] px-1 rounded">Anu the Sublime and Bel</mark>, the lord of Heaven and earth,
      who decreed the fate of the land, assigned to Marduk dominion over earthly man, they
      called <mark className="bg-emerald-200 px-1 rounded">Babylon</mark> by his illustrious name, made it great
      on earth, and founded an <mark className="bg-sky-200 px-1 rounded">everlasting kingdom</mark> in it.
    </p>
    <div className="mt-4 pt-4 border-t border-[#EBE7E0] text-xs text-[#5A6577] italic">
      &ldquo;Hammurabi calls himself &lsquo;divinely appointed&rsquo; — pattern repeats in Persian and Egyptian royal claims.&rdquo;
      <div className="text-[11px] text-[#A0AEC0] mt-1">— student note, 2:14 PM</div>
    </div>
  </div>
);

const QuizPreview = () => {
  const [picked, setPicked] = useState(null);
  const opts = [
    { label: 'A river valley civilization in modern-day Iraq', correct: true },
    { label: 'A type of cuneiform writing surface',            correct: false },
    { label: 'A Greek philosophical school',                    correct: false },
    { label: 'An ancient Egyptian trade route',                 correct: false },
  ];
  return (
    <Card className="p-6 bg-[#FDFBF7]">
      <div className="text-xs text-[#5A6577] mb-2">Question 1 of 12</div>
      <p className="font-medium text-[#14213D] mb-4">What is Mesopotamia?</p>
      <div className="space-y-2">
        {opts.map((o, i) => {
          const isCorrect = picked !== null && o.correct;
          const isWrong = picked === i && !o.correct;
          return (
            <button
              key={o.label}
              onClick={() => setPicked(i)}
              className={`w-full text-left p-3 rounded-lg border transition text-sm
                ${isCorrect ? 'border-emerald-400 bg-emerald-50 text-emerald-900' : ''}
                ${isWrong ? 'border-red-400 bg-red-50 text-red-900' : ''}
                ${picked === null ? 'border-[#EBE7E0] hover:border-[#14213D]/30 hover:bg-[#F9F8F4] cursor-pointer' : 'border-[#EBE7E0]'}
              `}
              data-testid={`tour-quiz-opt-${i}`}
            >
              <span className="font-mono text-xs text-[#5A6577] mr-2">{String.fromCharCode(65 + i)}.</span>
              {o.label}
              {isCorrect && <CheckCircle2 className="inline h-4 w-4 ml-2 text-emerald-600" />}
            </button>
          );
        })}
      </div>
    </Card>
  );
};

const SkeletonPreview = () => (
  <Card className="p-6 bg-[#FDFBF7] font-serif">
    <div className="text-xs uppercase tracking-widest text-[#5A6577] mb-3">Lesson 1 — Guided Notes</div>
    <h4 className="text-lg text-[#14213D] mb-4">The Rise of Sumer</h4>
    <div className="space-y-3 text-[#2D3748] text-sm leading-relaxed">
      <p>1. The first cities emerged along the <span className="inline-block w-32 border-b border-[#CBD5E0] mx-1" /> and <span className="inline-block w-32 border-b border-[#CBD5E0] mx-1" /> rivers.</p>
      <p>2. The Sumerian system of writing was called <span className="inline-block w-24 border-b border-[#CBD5E0] mx-1" />.</p>
      <p>3. The ziggurat served three primary purposes: <span className="inline-block w-20 border-b border-[#CBD5E0] mx-1" />, <span className="inline-block w-20 border-b border-[#CBD5E0] mx-1" />, and <span className="inline-block w-20 border-b border-[#CBD5E0] mx-1" />.</p>
      <p>4. Hammurabi&apos;s code is significant because <span className="inline-block w-40 border-b border-[#CBD5E0] mx-1" />.</p>
    </div>
  </Card>
);

const LecturePreview = () => (
  <Card className="p-0 overflow-hidden bg-[#0F1A2E] text-[#F9F8F4]">
    <div className="px-6 py-4 bg-[#14213D]/50 border-b border-[#2D3748] flex items-center justify-between text-xs">
      <span className="font-mono text-yellow-400">Lecture 2 of 4 · 12 min</span>
      <span className="text-[#A0AEC0]">Mesopotamia &gt; Religion &amp; Society</span>
    </div>
    <div className="p-8 min-h-[220px]">
      <h4 className="text-2xl font-serif mb-6">Polytheism &amp; the Cosmos</h4>
      <ul className="space-y-2 text-sm">
        <li>• Each city-state had a patron deity housed in the ziggurat.</li>
        <li>• Religion explained natural disasters and crop cycles.</li>
        <li>• The Enuma Elish: a creation myth predating Genesis by &gt;1000 years.</li>
      </ul>
    </div>
    <div className="px-6 py-3 bg-[#14213D]/50 flex gap-2 text-xs text-[#A0AEC0]">
      <span className="px-2 py-1 rounded bg-[#14213D]">Discussion: Why do societies create gods?</span>
    </div>
  </Card>
);

const TimelinePreview = () => {
  const events = [
    { year: '3500 BCE', label: 'Sumerian city-states rise',  color: 'bg-[#D4AF37]' },
    { year: '2334 BCE', label: 'Akkadian Empire founded',    color: 'bg-orange-500' },
    { year: '1792 BCE', label: 'Hammurabi takes the throne', color: 'bg-rose-500' },
    { year: '539 BCE',  label: 'Cyrus conquers Babylon',     color: 'bg-purple-500' },
  ];
  return (
    <Card className="p-6 bg-[#FDFBF7]">
      <div className="relative pl-6">
        <div className="absolute left-2 top-2 bottom-2 w-px bg-[#CBD5E0]" />
        {events.map((e) => (
          <div key={e.year} className="relative mb-6 last:mb-0">
            <div className={`absolute -left-[18px] top-1 w-3 h-3 rounded-full ${e.color} ring-4 ring-archive-paper`} />
            <div className="text-xs font-mono text-[#5A6577]">{e.year}</div>
            <div className="text-sm text-[#14213D] font-medium">{e.label}</div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const QuestionsPreview = () => (
  <Card className="p-6 bg-[#FDFBF7]">
    <div className="text-xs uppercase tracking-widest text-[#5A6577] mb-3">Today&apos;s Bell-Ringer</div>
    <ol className="space-y-4 list-decimal list-inside text-sm text-[#14213D] leading-relaxed">
      <li>Why did the world&apos;s first cities form near rivers? Name two specific advantages and one disadvantage.</li>
      <li>If Hammurabi&apos;s Code is the &ldquo;first written law,&rdquo; what informal laws existed before it?</li>
      <li>The Code says &ldquo;an eye for an eye.&rdquo; Is this justice? Defend with evidence.</li>
    </ol>
  </Card>
);

const EssayGraderPreview = () => (
  <Card className="p-6 bg-[#FDFBF7]">
    <div className="flex items-center justify-between mb-5">
      <div>
        <div className="text-xs text-[#5A6577] mb-1">Aria K. · Submitted 2:47 PM</div>
        <h4 className="font-serif text-lg text-[#14213D]">Why Did Mesopotamian Civilization Endure?</h4>
      </div>
      <div className="text-right">
        <div className="text-4xl font-serif text-yellow-600 leading-none">B+</div>
        <div className="text-xs text-[#5A6577] mt-1">87 / 100</div>
      </div>
    </div>
    <div className="space-y-3">
      {[
        { name: 'Thesis & Argument',           score: 4, max: 4 },
        { name: 'Evidence & Support',          score: 3, max: 4 },
        { name: 'Analysis & Critical Thinking',score: 3, max: 4 },
        { name: 'Organization & Writing',      score: 4, max: 4 },
      ].map((r) => (
        <div key={r.name}>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-[#2D3748]">{r.name}</span>
            <span className="font-mono text-[#5A6577]">{r.score} / {r.max}</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#EBE7E0] overflow-hidden">
            <div className="h-full bg-[#D4AF37] rounded-full" style={{ width: `${(r.score / r.max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
    <div className="mt-5 pt-4 border-t border-[#EBE7E0] grid grid-cols-2 gap-3 text-xs">
      <div>
        <div className="text-emerald-700 font-medium mb-1">Strengths</div>
        <div className="text-[#5A6577]">Strong thesis, well-cited evidence from Hammurabi&apos;s Code.</div>
      </div>
      <div>
        <div className="text-[#14213D] font-medium mb-1">Grow</div>
        <div className="text-[#5A6577]">Deepen analysis of the irrigation–trade feedback loop.</div>
      </div>
    </div>
  </Card>
);

const MapsPreview = () => (
  <Card className="p-0 overflow-hidden bg-[#F9F8F4]">
    <div className="relative aspect-[16/9] bg-gradient-to-br from-[#FDFBF7] via-stone-100 to-emerald-50">
      <svg viewBox="0 0 400 225" className="w-full h-full">
        <path d="M120 80 Q140 60 170 70 Q190 90 200 130 Q210 170 180 190 L160 195 Q140 175 130 150 Q115 110 120 80 Z" fill="#d4d4d4" stroke="#737373" strokeWidth="1" />
        <path d="M240 60 Q280 50 310 90 Q330 130 305 170 Q280 200 245 180 L230 150 Q225 110 240 60 Z" fill="#e7e5e4" stroke="#737373" strokeWidth="1" />
        <circle cx="155" cy="130" r="5" fill="#dc2626"><title>Babylon</title></circle>
        <circle cx="180" cy="160" r="4" fill="#ea580c"><title>Ur</title></circle>
        <circle cx="275" cy="115" r="4" fill="#16a34a"><title>Susa</title></circle>
        <text x="160" y="125" fontSize="9" fill="#1c1917" fontFamily="serif">Babylon</text>
        <text x="185" y="155" fontSize="9" fill="#1c1917" fontFamily="serif">Ur</text>
        <text x="280" y="110" fontSize="9" fill="#1c1917" fontFamily="serif">Susa</text>
      </svg>
    </div>
    <div className="p-4 flex items-center gap-2 text-xs text-[#5A6577] bg-[#FDFBF7] border-t border-[#EBE7E0]">
      <Badge variant="outline" className="text-[11px]">8 markers</Badge>
      <span>Mesopotamia · trade cities, ziggurat sites, Code of Hammurabi find location</span>
    </div>
  </Card>
);

const VideoPreview = () => {
  const vids = [
    // Verified working YouTube video IDs (Crash Course World History / CNN 10 archives).
    // Falls back to a gold-tinted placeholder if YouTube ever takes one down.
    { id: 'sohXPx_XZ6Y', title: 'Mesopotamia · Crash Course',        host: 'John Green',     mins: '11:32' },
    { id: 'Q-mkVSasZIM', title: 'The Persians & Greeks',              host: 'Crash Course',   mins: '12:43' },
    { id: 'rNu8XDBSn10', title: "Current Events Daily Brief",         host: 'Daily News',     mins: '10:00' },
  ];
  const handleImgError = (e) => {
    // YouTube returns 404 for any thumbnail variant of a deleted/private video.
    // Hide the broken image and let the gold play-button + title speak for itself.
    e.currentTarget.style.display = 'none';
  };
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      {vids.map((v) => (
        <Card key={v.id} className="p-0 overflow-hidden">
          <div className="relative aspect-video bg-gradient-to-br from-[#14213D] to-[#0F1A2E]">
            <img
              src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`}
              alt={v.title}
              className="w-full h-full object-cover opacity-90"
              onError={handleImgError}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-[#FDFBF7]/90 flex items-center justify-center">
                <PlayCircle className="h-6 w-6 text-[#14213D]" />
              </div>
            </div>
            <div className="absolute bottom-1 right-1 text-[11px] font-mono px-1.5 py-0.5 rounded bg-[#0F1A2E]/80 text-[#F9F8F4]">{v.mins}</div>
          </div>
          <div className="p-3">
            <div className="text-xs font-medium text-[#14213D] leading-snug line-clamp-2">{v.title}</div>
            <div className="text-[11px] text-[#5A6577] mt-1">{v.host}</div>
          </div>
        </Card>
      ))}
    </div>
  );
};

// ── Tour page ──────────────────────────────────────────────────────────────
export default function TourPage() {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState('library');
  const [demoUnitId, setDemoUnitId] = useState(DEMO_UNIT_ID_FALLBACK);
  const sectionRefs = useRef({});

  // Resolve the demo unit id — verify seeded id exists, else use first G9 unit
  useEffect(() => {
    let cancelled = false;
    const resolveDemoUnit = async () => {
      try {
        await axios.get(`${API}/units/${DEMO_UNIT_ID_FALLBACK}`);
        // seeded id is valid; keep fallback
      } catch {
        try {
          const res = await axios.get(`${API}/units/grade/9`);
          if (!cancelled && Array.isArray(res.data) && res.data[0]?.id) {
            setDemoUnitId(res.data[0].id);
          }
        } catch (err) {
          console.warn('TourPage: demo unit fetch failed, using fallback id', err);
        }
      }
    };
    resolveDemoUnit();
    return () => { cancelled = true; };
  }, []);

  // Track which stop is in view for the sticky stepper
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-30% 0px -50% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    TOOL_STOPS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) {
        sectionRefs.current[s.id] = el;
        observer.observe(el);
      }
    });
    return () => observer.disconnect();
  }, []);

  const goTo = (path) => navigate(path);

  const activeIndex = useMemo(
    () => Math.max(0, TOOL_STOPS.findIndex((s) => s.id === activeId)),
    [activeId]
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Top nav */}
      <nav className="glass-nav sticky top-0 z-30 border-b border-[#EBE7E0]/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#14213D] hover:text-[#D4AF37] transition"
            data-testid="tour-home-btn"
          >
            <Home className="h-4 w-4" />
            <span className="font-serif-display text-sm tracking-wide">Atlas History</span>
          </button>
          <div className="hidden md:flex items-center gap-3 text-xs text-[#5A6577]">
            <span className="font-mono">Step {activeIndex + 1} / {TOOL_STOPS.length}</span>
            <div className="w-40 h-1 bg-[#EBE7E0] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#D4AF37] transition-all duration-500"
                style={{ width: `${((activeIndex + 1) / TOOL_STOPS.length) * 100}%` }}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="tour-login-btn">
              Sign In
            </Button>
            <Button size="sm" className="btn-primary" onClick={() => navigate('/subscription')} data-testid="tour-subscribe-btn">
              Subscribe
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FDFBF7]/60 via-archive-paper to-emerald-50/40 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-36">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0F1A2E] text-archive-paper text-xs uppercase tracking-widest mb-6">
            <Sparkles className="h-3 w-3" />
            Guided product tour
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-serif font-light tracking-tight text-[#14213D] mb-6 max-w-4xl">
            Every tool you&apos;d build for a year of social studies — in twelve stops.
          </h1>
          <p className="text-lg sm:text-xl text-[#5A6577] max-w-2xl mb-10 leading-relaxed">
            Walk through the platform with our demo unit: <em className="text-[#14213D] not-italic font-medium">Grade 9, Ancient Mesopotamia</em>.
            Each stop is interactive — no sign-in needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              size="lg"
              className="btn-primary group"
              onClick={() => navigate(`/unit/${demoUnitId}`)}
              data-testid="tour-open-full-unit-btn"
            >
              Open the full preview unit
              <ChevronDown className="h-4 w-4 ml-2 -rotate-90 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => document.getElementById('library')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="tour-start-btn"
            >
              Or walk the guided tour
              <ChevronDown className="h-4 w-4 ml-2 transition-transform group-hover:translate-y-0.5" />
            </Button>
          </div>
          <p className="mt-4 text-sm text-[#5A6577] max-w-xl">
            <span className="font-semibold text-[#14213D]">Free with any account</span> — the entire
            <em className="not-italic"> Grade 9 · Ancient Mesopotamia</em> unit is fully open: primary sources,
            skeleton notes, lecture mode, quizzes, essay grader, PDF downloads. No card, no gating.
          </p>

          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl pt-10 border-t border-[#EBE7E0]">
            {[
              ['78',  'curriculum units'],
              ['900+', 'primary sources'],
              ['118', 'curated videos'],
              ['24',  'state assessments'],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="text-3xl font-serif text-[#14213D]">{n}</div>
                <div className="text-xs uppercase tracking-widest text-[#5A6577] mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Stops */}
      <main className="relative">
        <Stop id="library" index={1} icon={Library} title="Curriculum Library" tagline={TOOL_STOPS[0].tagline} onCta={() => goTo('/dashboard/9')}>
          <LibraryPreview />
        </Stop>

        <Stop id="unit" index={2} icon={BookOpen} title="Unit Detail" tagline={TOOL_STOPS[1].tagline} onCta={() => goTo(`/unit/${demoUnitId}`)}>
          <UnitPreview />
        </Stop>

        <Stop id="sources" index={3} icon={FileText} title="Primary Sources" tagline={TOOL_STOPS[2].tagline} onCta={() => goTo(`/primary-sources/${demoUnitId}`)}>
          <SourcesPreview />
        </Stop>

        <Stop id="annotate" index={4} icon={Highlighter} title="Annotation Tool" tagline={TOOL_STOPS[3].tagline} onCta={() => goTo(`/annotate/${demoUnitId}/0`)}>
          <AnnotatePreview />
        </Stop>

        <Stop id="quiz" index={5} icon={Brain} title="Vocabulary Quiz" tagline={TOOL_STOPS[4].tagline} onCta={() => goTo(`/quiz/${demoUnitId}`)}>
          <QuizPreview />
        </Stop>

        <Stop id="skeleton" index={6} icon={ClipboardList} title="Skeleton Notes" tagline={TOOL_STOPS[5].tagline} onCta={() => goTo(`/skeleton-notes/${demoUnitId}`)}>
          <SkeletonPreview />
        </Stop>

        <Stop id="lecture" index={7} icon={Presentation} title="Lecture Presentation" tagline={TOOL_STOPS[6].tagline} onCta={() => goTo(`/lecture-presentation/${demoUnitId}`)}>
          <LecturePreview />
        </Stop>

        <Stop id="timeline" index={8} icon={Clock} title="Historical Timeline" tagline={TOOL_STOPS[7].tagline} onCta={() => goTo('/timeline')}>
          <TimelinePreview />
        </Stop>

        <Stop id="questions" index={9} icon={MessageSquare} title="Daily Questions" tagline={TOOL_STOPS[8].tagline} onCta={() => goTo('/daily-questions')}>
          <QuestionsPreview />
        </Stop>

        <Stop id="essay" index={10} icon={GraduationCap} title="AI Essay Grader" tagline={TOOL_STOPS[9].tagline} onCta={() => goTo(`/essay-grader/${demoUnitId}`)}>
          <EssayGraderPreview />
        </Stop>

        <Stop id="maps" index={11} icon={Map} title="Map Explorer" tagline={TOOL_STOPS[10].tagline} onCta={() => goTo('/maps')}>
          <MapsPreview />
        </Stop>

        <Stop id="video" index={12} icon={PlayCircle} title="Video Library" tagline={TOOL_STOPS[11].tagline} onCta={() => goTo('/videos')}>
          <VideoPreview />
        </Stop>

        {/* Final CTA */}
        <section className="border-t border-[#EBE7E0]/60 py-24 lg:py-32" data-testid="tour-final-cta">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FBF6E8] text-[#14213D] text-xs uppercase tracking-widest mb-6">
              <Star className="h-3 w-3" /> Built by Kansas teachers
            </div>
            <h2 className="text-4xl sm:text-5xl font-serif font-light tracking-tight text-[#14213D] mb-6">
              Ready to bring this to your classroom?
            </h2>
            <p className="text-lg text-[#5A6577] mb-10 leading-relaxed">
              Start with the Basic tier ($10/mo or $100/yr) to browse the curriculum, or unlock every
              tool above with Pro ($25/mo or $250/yr).
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" className="btn-primary" onClick={() => navigate('/login')} data-testid="tour-create-account-btn">
                Create your account
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/about')} data-testid="tour-learn-more-btn">
                Learn more
              </Button>
            </div>
            <div className="mt-10 flex items-center justify-center gap-2 text-xs text-[#5A6577]">
              <Shield className="h-3 w-3" />
              Aligned to Kansas HGSS Standards
            </div>
          </div>
        </section>
      </main>

      {/* Floating stepper (desktop only) */}
      <aside className="hidden xl:flex flex-col gap-2 fixed left-6 top-1/2 -translate-y-1/2 z-20" aria-label="Tour progress">
        {TOOL_STOPS.map((s, i) => {
          const isActive = s.id === activeId;
          return (
            <a
              key={s.id}
              href={`#${s.id}`}
              className={`group flex items-center gap-3 transition-all ${isActive ? 'text-[#14213D]' : 'text-[#A0AEC0] hover:text-[#2D3748]'}`}
              data-testid={`tour-nav-${s.id}`}
            >
              <span
                className={`block rounded-full transition-all
                  ${isActive ? 'w-3 h-3 bg-[#D4AF37] ring-4 ring-[#D4AF37]/30' : 'w-2 h-2 bg-[#A0AEC0] group-hover:bg-[#1D2D50]'}`}
              />
              <span className={`text-xs font-mono transition-opacity ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                {String(i + 1).padStart(2, '0')} {s.title}
              </span>
            </a>
          );
        })}
      </aside>
    </div>
  );
}
